import { useState } from "react";

import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { notifSilence, notifSilenceError, notifSilenceWarning } from "@/core/utils/func/notifApp";
import { useParty } from "@/features/kyc/api/use-party";
import { useKycTranslation } from "@/features/kyc/i18n/use-kyc-translation";
import { useSubmitApprovalRequest } from "@/features/shared/api/use-submit-approval-request";

/**
 * Forçage du criblage sanctions via maker-checker (`KYC_FORCE_OVERRIDE`) —
 * action protégée §5.4 / 8.2, aussi disponible hors wizard d'inscription.
 */
export function PartySanctionsOverride({
  partyId,
  currentStatus,
}: {
  partyId: string;
  currentStatus?: string;
}) {
  const { t: tKyc } = useKycTranslation();
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [requested, setRequested] = useState(false);
  const submitApproval = useSubmitApprovalRequest();
  const { refetch, isFetching } = useParty(partyId);

  if (currentStatus === "CLEAR") return null;

  return (
    <RequireRole roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}>
      <div className="flex max-w-md flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          {tKyc("kyc.onboarding.screening.pendingApproval")}
        </p>
        {requested ? (
          <Button
            variant="outline"
            className="self-start"
            disabled={isFetching}
            onClick={async () => {
              const { data } = await refetch();
              if (data?.sanctionsScreeningStatus !== "CLEAR") {
                notifSilenceWarning(tKyc("kyc.onboarding.screening.stillPending"));
              }
            }}
          >
            {isFetching ? <ButtonSpinner /> : null}
            {tKyc("kyc.onboarding.screening.recheck")}
          </Button>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="party-screening-ref">
                {tKyc("kyc.onboarding.screening.reference")}
              </Label>
              <Input
                id="party-screening-ref"
                className="notranslate"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={tKyc("kyc.onboarding.screening.referencePlaceholder")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="party-screening-reason">
                {tKyc("kyc.onboarding.screening.reason")}
              </Label>
              <Input
                id="party-screening-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={tKyc("kyc.onboarding.screening.reasonPlaceholder")}
              />
            </div>
            <Button
              className="self-start"
              disabled={submitApproval.isPending || !reason.trim()}
              onClick={() =>
                submitApproval.mutate(
                  {
                    actionType: "KYC_FORCE_OVERRIDE",
                    sourceService: "kyc-service",
                    resourceType: "Party",
                    resourceId: partyId,
                    payload: {
                      status: "CLEAR",
                      ...(reference.trim()
                        ? { reference: reference.trim() }
                        : {}),
                    },
                    reason: reason.trim(),
                  },
                  {
                    onSuccess: () => {
                      notifSilence(tKyc("kyc.onboarding.screening.requested"));
                      setRequested(true);
                    },
                    onError: (error) =>
                      notifSilenceError(apiErrorHandler(error)),
                  }
                )
              }
            >
              {submitApproval.isPending ? <ButtonSpinner /> : null}
              {tKyc("kyc.onboarding.screening.submitForApproval")}
            </Button>
          </>
        )}
      </div>
    </RequireRole>
  );
}
